import React, { useState } from 'react';
import { services } from '../data/services';
import logo from '../assets/logo.jpg';
import SEO from '../components/SEO';
import ServicesSchemaData from '../data/seo/ServicesPageSchema';
import toast, { Toaster } from 'react-hot-toast';

const Services: React.FC = () => {

  // Brand colors for cycling through service cards
  const brandColors = [
    'masa-coral',
    'masa-yellow',
    'masa-teal',
    'masa-blue',
    'masa-magenta'
  ];

  const brandGradients = [
    'from-masa-coral to-masa-magenta',
    'from-masa-yellow to-masa-coral',
    'from-masa-teal to-masa-blue',
    'from-masa-blue to-masa-teal',
    'from-masa-magenta to-masa-coral'
  ];
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  // Handle service checkbox changes
  const handleCheckboxChange = (service: any) => {
    setSelectedServices(prev => {
      if (service === 'All Services') {
        // If "All Services" is selected, clear all other selections
        return prev.includes('All Services') ? [] : ['All Services'];
      } else {
        // If any specific service is selected, remove "All Services" if it was selected
        const newServices = prev.filter(s => s !== 'All Services');
        
        if (prev.includes(service)) {
          // Remove the service if it's already selected
          return newServices.filter(s => s !== service);
        } else {
          // Add the service if it's not selected
          return [...newServices, service];
        }
      }
    });
  };

  // Form validation
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Please enter your full name');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return false;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return false;
    }
    
    if (!formData.message.trim()) {
      setError('Please enter a message');
      return false;
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setError('');
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare the data to send
      const submissionData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        subject: selectedServices.length > 0 
          ? `Service Inquiry: ${selectedServices.join(', ')}` 
          : 'General Service Inquiry',
        message: `Services Interested In: ${selectedServices.length > 0 ? selectedServices.join(', ') : 'Not specified'}\n\nMessage:\n${formData.message.trim()}`
      };

      // Send to your c-masa.php API
      //const response = await fetch('http://localhost/masa-backend/api/c-masa.php', {
      const response = await fetch('https://masahillshealthservices.com/api/c-masa.php', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      if (response.ok && result.success) {

        // Success - Show toast instead of setting submitted state
      toast.success('Your message has been sent successfully!', {
                    duration: 4000,
                    position: 'top-center',
                    style: {
                    background: '#10B981',
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '16px',
                    },
                });
        // Success
        setSubmitted(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        setSelectedServices([]);
        
        // Hide success message after 5 seconds
       /*  setTimeout(() => {
          setSubmitted(false);
        }, 5000); */
        
      } else {
        // Handle API error
        throw new Error(result.error || 'Failed to submit inquiry');
      }
      
    } catch (err) {
      console.error('Submission error:', err);
      setError(
        typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message?: string }).message)
          : 'Failed to submit inquiry. Please try again.'
      );
      toast.error(
        typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message?: string }).message)
          : 'Failed to submit inquiry. Please try again.',
        {
          duration: 5000,
          position: 'top-center',
        }
      );


    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="bg-white text-masa-dark font-sans">
      <SEO
        title={ServicesSchemaData.pageTitle}
        description={ServicesSchemaData.pageDescription}
        keywords={ServicesSchemaData.pageKeywords}
        canonical={ServicesSchemaData.canonicalUrl}
        schema={ServicesSchemaData.servicesSchemaJson}
        openGraph={
          {
            title: ServicesSchemaData.pageTitle,
            description: ServicesSchemaData.pageDescription,
            url: ServicesSchemaData.canonicalUrl,
            type: 'website',
            image: logo, // Assuming logo is the main image for OG
          }
        }
      />
      {/* Page Header */}
      <section className="bg-gradient-to-br from-gray-50 to-masa-blue/5 py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Brand accent line */}
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-1 bg-gradient-to-r from-masa-coral via-masa-yellow via-masa-teal via-masa-blue to-masa-magenta rounded-full"></div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-masa-coral to-masa-teal">home care Services</span>
          </h1>

          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-masa-blue/10">
              <h2 className="text-xl font-semibold text-masa-blue mb-4">
                MASA HILLS Health Services - Comprehensive Care Solutions
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                At MASA HILLS Health Services, we provide compassionate and personalized assistance designed to
                enhance the quality of life and promote independence for clients, whether they reside in their own
                home, an assisted living facility, or a skilled nursing facility. We meticulously tailor a detailed care
                program based on each client's current needs and preferences and continuously adapt our care plan as
                their requirements evolve, ensuring truly dynamic and responsive support.
              </p>

              {/* Brand color dots */}
              <div className="flex items-center justify-center space-x-3 mt-6">
                <div className="w-3 h-3 bg-masa-coral rounded-full"></div>
                <div className="w-3 h-3 bg-masa-yellow rounded-full"></div>
                <div className="w-3 h-3 bg-masa-teal rounded-full"></div>
                <div className="w-3 h-3 bg-masa-blue rounded-full"></div>
                <div className="w-3 h-3 bg-masa-magenta rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const colorIndex = index % brandColors.length;
              const accentColor = brandColors[colorIndex];
              const gradientClass = brandGradients[colorIndex];

              return (
                <div
                  key={service.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-masa-blue/20"
                >
                  {/* Brand accent top border */}
                  <div className={`h-1 bg-gradient-to-r ${gradientClass}`}></div>

                  <div className="relative overflow-hidden aspect-[4/3] bg-white flex items-center justify-center">
                    <img
                      src={service.image}
                      alt={`MASA HILLS Health Services - ${service.title}`}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Brand color overlay on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  </div>

                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 bg-${accentColor} rounded-lg flex items-center justify-center`}>
                        <span className="text-xl text-white">{service.icon}</span>
                      </div>
                      <h2 className="text-xl font-bold text-masa-dark">{service.title}</h2>
                    </div>

                    <p className="text-sm text-gray-600 leading-relaxed">{service.shortDesc}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">{service.longDesc}</p>

                    {/* Service quality indicators */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 bg-${accentColor} rounded-full`}></div>
                        <span className="text-xs font-medium text-masa-dark">MASA HILLS Service</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-3 h-3 text-masa-yellow fill-current" viewBox="0 0 20 20">
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Footer with Logo and Contact Form */}
      <section className="bg-gradient-to-br from-gray-50 to-masa-blue/5 py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
          {/* Logo and Info */}
          <div className="space-y-8 text-center">
            <div className="relative">
              <img
                src={logo}
                alt="MASA HILLS Health Services Logo"
                className="h-auto w-auto mx-auto rounded-xl shadow-xl hover:shadow-2xl transition-shadow duration-300 border-4 border-white"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-3xl font-bold text-masa-dark">
                Let's <span className="text-transparent bg-clip-text bg-gradient-to-r from-masa-coral to-masa-teal">Connect</span>
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed max-w-md mx-auto">
                Ready to get started or have questions about a service? Fill out the form and we'll be in touch within 24 hours.
              </p>

              {/* Contact highlights */}
              <div className="space-y-3 mt-6">
                <div className="flex items-center justify-center space-x-3">
                  <div className="w-3 h-3 bg-masa-teal rounded-full"></div>
                  <span className="text-sm font-medium text-masa-dark">Free Consultation</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <div className="w-3 h-3 bg-masa-coral rounded-full"></div>
                  <span className="text-sm font-medium text-masa-dark">24/7 Support Available</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <div className="w-3 h-3 bg-masa-blue rounded-full"></div>
                  <span className="text-sm font-medium text-masa-dark">Licensed Professionals</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-masa-blue/10">
            {/* Form header with brand colors */}
            <div className="bg-gradient-to-r from-masa-blue to-masa-teal p-6 text-white">
              <h4 className="text-xl font-bold mb-2">Get Started Today</h4>
              <p className="text-white/90 text-sm">Complete the form below for your free consultation</p>
            </div>
            {/* Contact Form */}
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="name" className="block font-semibold text-masa-dark mb-2">Full Name*</label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-masa-coral focus:border-masa-coral transition-all duration-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block font-semibold text-masa-dark mb-2">Email*</label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-masa-teal focus:border-masa-teal transition-all duration-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block font-semibold text-masa-dark mb-2">Phone Number*</label>
                <input
                  type="tel"
                  id="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-masa-blue focus:border-masa-blue transition-all duration-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block font-semibold text-masa-dark mb-2">Service Interested In</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedServices.includes('All Services')}
                      onChange={() => handleCheckboxChange('All Services')}
                      disabled={isSubmitting}
                      className="form-checkbox text-masa-magenta mr-2 disabled:cursor-not-allowed"
                    />
                    <span className={isSubmitting ? 'text-gray-400' : ''}>All Services</span>
                  </label>
                  {services && services.map(service => (
                    <label key={service.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={
                          selectedServices.includes('All Services')
                            ? false
                            : selectedServices.includes(service.title)
                        }
                        onChange={() => handleCheckboxChange(service.title)}
                        disabled={selectedServices.includes('All Services') || isSubmitting}
                        className="form-checkbox text-masa-magenta mr-2 disabled:cursor-not-allowed"
                      />
                      <span className={isSubmitting || selectedServices.includes('All Services') ? 'text-gray-400' : ''}>
                        {service.title}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block font-semibold text-masa-dark mb-2">Message*</label>
                <textarea
                  id="message"
                  rows={4}
                  required
                  value={formData.message}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-masa-yellow focus:border-masa-yellow transition-all duration-300 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Tell us about your care needs..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-masa-coral to-masa-magenta text-white font-semibold py-4 rounded-lg hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] hover:from-masa-coral/90 hover:to-masa-magenta/90 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-none"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </div>
                ) : (
                  'Submit Inquiry'
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                By submitting this form, you agree to be contacted by MASA HILLS Health Services regarding your inquiry.
              </p>
            </form>
          </div>
        </div>
      </section>
      <Toaster />
    </div>
  );
};

export default Services;